/* eslint-disable react/no-array-index-key */
import {Typography, css, useTheme} from '@mui/material';
import {GetStylesFunction, DlMuiChip} from '@wallaby-ui/core';
import * as React from 'react';
import {
  AnnotationTooltipRiskFactorLabel,
  Formatters,
  MerieSchema,
  RiskFactorLabelEnum,
  RiskFactorTypeEnum,
  resolveMedicationUsageTypeChip,
} from 'src/js/common';
import {getFormattedAnnotationParts, getFormattedAnnotationString} from 'src/js/containers/hospitalizations/hospitalizations_helpers';

import {TimelineAnnotation} from './TimelineAnnotation';

interface OwnProps {
  versionSuffix: string;
  encounterId: number | undefined;
  data?: MerieSchema.components['schemas']['TimelineEventAnnotationDto'][];
  riskFactorToRiskFactorLabel: Record<RiskFactorTypeEnum, RiskFactorLabelEnum>;
  formatters: Formatters;
}

type Props = OwnProps;

export const MedicationAnnotation: React.FC<Props> = (props) => {
  const {versionSuffix, encounterId, data, riskFactorToRiskFactorLabel, formatters} = props;
  const theme = useTheme();
  const styles = getStyles({theme, params: {}});
  const {t} = formatters;
  const unknownText = t('global.unknown');

  return (
    <>
      {data?.map((annotation, i) => {
        const [_atc, medicationName, dosage, routeOfAdministration, _] = getFormattedAnnotationParts(annotation, formatters);
        const annotationText = getFormattedAnnotationString(annotation, formatters);
        // PDW-48 bug fix: previously the chip was always rendered and showed
        // raw "UNKNOWN" text when no usageType was set on the annotation.
        // Now we only render it for clear states (Discontinued / Acute /
        // Chronic). Shared color scheme matches the placeholder table chip.
        const usageTypeChip = resolveMedicationUsageTypeChip({
          usageType: annotation.medicationData?.usageType,
          isDiscontinued: annotation.medicationData?.isDiscontinued,
          formatters,
          theme,
          forceShow: false,
        });
        const activeSubstanceTexts = annotation.medicationData?.ingredientLabels ?? [];
        const routeOfAdministrationText = routeOfAdministration ?? unknownText;
        const dosageText = dosage ?? unknownText;
        const amountText = annotation.medicationData?.amount ?? unknownText;
        const atcTextsCount = Math.max(
          (annotation.medicationData?.atcCodes ?? []).length,
          (annotation.medicationData?.atc4thLevelLabels ?? []).length
        );
        const atcTexts = Array(atcTextsCount)
          .fill(0)
          .map((_, i) => {
            const atcCode = annotation.medicationData?.atcCodes?.[i] ?? unknownText;
            const atc3rdDescription = annotation.medicationData?.atc3rdLevelDescription ?? unknownText;
            const atc4thLabel = annotation.medicationData?.atc4thLevelLabels?.[i] ?? unknownText;
            return `${atcCode} (${atc3rdDescription} - ${atc4thLabel})`;
          });
        const noteText = annotation.medicationData?.comment
          ? annotation.medicationData?.comment
          : t('timeline.annotationTooltip.note.missing');
        const annotationTooltip = (
          <div css={styles.annotationWrapper}>
            <div css={styles.sectionWrapper}>
              <Typography variant='value'>{medicationName}</Typography>
            </div>
            {usageTypeChip && (
              <div css={styles.sectionWrapper}>
                <DlMuiChip
                  label={usageTypeChip.label}
                  style={{backgroundColor: usageTypeChip.backgroundColor}}
                  whiteText={usageTypeChip.whiteText}
                />
              </div>
            )}
            <div css={styles.sectionWrapper}>
              <Typography variant='labelBold'>{t('timeline.annotationTooltip.activeSubstance')}</Typography>
              {activeSubstanceTexts.length === 0 && <Typography variant='value'>{unknownText}</Typography>}
              {activeSubstanceTexts.map((activeSubstanceText, i) => (
                <Typography key={i} variant='value'>
                  {activeSubstanceText}
                </Typography>
              ))}
            </div>
            <div css={styles.sectionWrapper}>
              <Typography variant='labelBold'>{t('timeline.annotationTooltip.dosage')}</Typography>
              <Typography variant='value'>{dosageText}</Typography>
            </div>
            <div css={styles.sectionWrapper}>
              <Typography variant='labelBold'>{t('timeline.annotationTooltip.amount')}</Typography>
              <Typography variant='value'>{amountText}</Typography>
            </div>
            <div css={styles.sectionWrapper}>
              <Typography variant='labelBold'>{t('timeline.annotationTooltip.routeOfAdministration')}</Typography>
              <Typography variant='value'>{routeOfAdministrationText}</Typography>
            </div>
            <div css={styles.sectionWrapper}>
              <Typography variant='labelBold'>{t('timeline.annotationTooltip.atc')}</Typography>
              {atcTexts.length === 0 && <Typography variant='value'>{unknownText}</Typography>}
              {atcTexts.map((atcText, i) => (
                <Typography key={i} variant='value'>
                  {atcText}
                </Typography>
              ))}
            </div>
            <div css={styles.sectionWrapper}>
              <Typography variant='labelBold'>{t('timeline.annotationTooltip.note')}</Typography>
              <Typography variant='value'>{noteText}</Typography>
            </div>
            <AnnotationTooltipRiskFactorLabel
              annotation={annotation}
              formatters={formatters}
              riskFactorToRiskFactorLabel={riskFactorToRiskFactorLabel}
            />
          </div>
        );
        return (
          <TimelineAnnotation
            key={`${annotation.documentVersionId}-${i}`}
            versionSuffix={versionSuffix}
            encounterId={encounterId}
            documentVersionId={annotation.documentVersionId}
            highlightType={annotation.highlightType}
            tooltip={annotationTooltip}
            isDiscontinued={annotation.medicationData?.isDiscontinued}
          >
            {annotationText}
          </TimelineAnnotation>
        );
      })}
    </>
  );
};

MedicationAnnotation.displayName = 'MedicationAnnotation';

const getStyles: GetStylesFunction = (options) => ({
  annotationWrapper: css`
    display: flex;
    flex-direction: column;
    gap: 8px;
  `,
  divider: css`
    border-top: 1px solid ${options?.theme?.palette.custom.coolGray['500']};
    width: 100%;
    height: 0;
  `,
  sectionWrapper: css`
    display: flex;
    flex-direction: column;
    align-items: start;
  `,
});
